export type Point = [number, number];

/**
 * A smooth SVG path through the points (monotone cubic, Fritsch–Carlson):
 * no corners at the samples and no overshoot past local highs and lows, so a
 * balance line never draws a value the account never had.
 */
export const monotonePath = (points: Point[]) => {
  const n = points.length;
  if (!n) return "";
  if (n === 1) return `M${points[0][0]},${points[0][1]}`;
  const dx: number[] = [];
  const slopes: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(points[i + 1][0] - points[i][0]);
    slopes.push(dx[i] ? (points[i + 1][1] - points[i][1]) / dx[i] : 0);
  }
  const tangents: number[] = [slopes[0]];
  for (let i = 1; i < n - 1; i++) {
    if (slopes[i - 1] * slopes[i] <= 0) tangents.push(0);
    else {
      const w1 = 2 * dx[i] + dx[i - 1];
      const w2 = dx[i] + 2 * dx[i - 1];
      tangents.push((w1 + w2) / (w1 / slopes[i - 1] + w2 / slopes[i]));
    }
  }
  tangents.push(slopes[n - 2]);

  const f = (value: number) => Math.round(value * 100) / 100;
  let d = `M${f(points[0][0])},${f(points[0][1])}`;
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = points[i];
    const [x1, y1] = points[i + 1];
    const h = dx[i] / 3;
    d += `C${f(x0 + h)},${f(y0 + tangents[i] * h)} ${f(x1 - h)},${f(
      y1 - tangents[i + 1] * h
    )} ${f(x1)},${f(y1)}`;
  }
  return d;
};

/**
 * Tangents for a curve that flows THROUGH its anchors: the slopes of a natural
 * cubic spline (so the curvature changes gently, with no knees), then limited
 * to the monotone range (Hyman filter): a flat tangent at every local high or
 * low and never steeper than 3x the adjacent chord slope. The result is C1 at
 * every point and never leaves the range of its neighbours.
 */
const smoothTangents = (points: Point[]): number[] => {
  const n = points.length;
  const dx: number[] = [];
  const slopes: number[] = [];
  for (let i = 0; i < n - 1; i++) {
    dx.push(points[i + 1][0] - points[i][0]);
    slopes.push(dx[i] ? (points[i + 1][1] - points[i][1]) / dx[i] : 0);
  }
  // Second derivatives of the natural spline (Thomas algorithm).
  const diagonal = new Array<number>(n).fill(1);
  const upper = new Array<number>(n).fill(0);
  const curvature = new Array<number>(n).fill(0);
  for (let i = 1; i < n - 1; i++) {
    const factor = dx[i - 1] / diagonal[i - 1];
    diagonal[i] = 2 * (dx[i - 1] + dx[i]) - factor * upper[i - 1];
    upper[i] = dx[i];
    curvature[i] = 6 * (slopes[i] - slopes[i - 1]) - factor * curvature[i - 1];
  }
  for (let i = n - 2; i >= 0; i--)
    curvature[i] = (curvature[i] - upper[i] * curvature[i + 1]) / diagonal[i];

  return points.map((_, i) => {
    const raw =
      i < n - 1
        ? slopes[i] - (dx[i] * (2 * curvature[i] + curvature[i + 1])) / 6
        : slopes[i - 1] + (dx[i - 1] * (curvature[i - 1] + 2 * curvature[i])) / 6;
    const before = i > 0 ? slopes[i - 1] : slopes[i];
    const after = i < n - 1 ? slopes[i] : slopes[i - 1];
    if (before * after <= 0 || raw * after <= 0) return 0;
    const limit = 3 * Math.min(Math.abs(before), Math.abs(after));
    return Math.sign(raw) * Math.min(Math.abs(raw), limit);
  });
};

/** Evaluates the smooth-through curve: `steps` samples per segment, plus the last anchor. */
export const smoothThroughSamples = (points: Point[], steps = 12): Point[] => {
  const n = points.length;
  if (n < 2) return [...points];
  const tangents = smoothTangents(points);
  const out: Point[] = [];
  for (let i = 0; i < n - 1; i++) {
    const w = points[i + 1][0] - points[i][0];
    for (let k = 0; k < steps; k++) {
      const s = k / steps;
      const s2 = s * s;
      const s3 = s2 * s;
      out.push([
        points[i][0] + w * s,
        (2 * s3 - 3 * s2 + 1) * points[i][1] +
          (s3 - 2 * s2 + s) * w * tangents[i] +
          (-2 * s3 + 3 * s2) * points[i + 1][1] +
          (s3 - s2) * w * tangents[i + 1],
      ]);
    }
  }
  out.push(points[n - 1]);
  return out;
};

/** One continuous SVG path through every anchor (see smoothTangents). */
export const smoothThroughPath = (points: Point[]) => {
  const n = points.length;
  if (!n) return "";
  if (n === 1) return `M${points[0][0]},${points[0][1]}`;
  const tangents = smoothTangents(points);
  const f = (value: number) => Math.round(value * 100) / 100;
  let d = `M${f(points[0][0])},${f(points[0][1])}`;
  for (let i = 0; i < n - 1; i++) {
    const [x0, y0] = points[i];
    const [x1, y1] = points[i + 1];
    const h = (x1 - x0) / 3;
    d += `C${f(x0 + h)},${f(y0 + tangents[i] * h)} ${f(x1 - h)},${f(
      y1 - tangents[i + 1] * h
    )} ${f(x1)},${f(y1)}`;
  }
  return d;
};
